import { Form } from '@/components/primitives/Form';
import AncillaryModalFooter from '@/components/purchase/checkout/ancillaries/ancillary-modal/AncillaryModalFooter';
import AncillaryOptions from '@/components/purchase/checkout/ancillaries/ancillary-modal/AncillaryOptions';
import LegInfo from '@/components/purchase/checkout/ancillaries/ancillary-modal/LegInfo';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import {
  addPurchaseFlowBookingAncillaries,
  getPurchaseFlowBookingById,
} from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingIdSelector } from '@/features/purchase/purchaseSelectors';
import { useTranslation } from '@/i18n';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import { type LegDTO } from '@/types/dto/agent';
import {
  type AncillaryValues,
  type CheckoutValues,
  createAncillarySchema,
} from '@/utils/zodSchema';
import { Divider, Typography } from '@fleet/webshop-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useCallback, useMemo } from 'react';
import { useForm, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

interface AncillaryModalProps {
  selectedLeg?: LegDTO;
  changeIsOverlayOpened: (isOpen: boolean) => void;
}

const AncillaryModal: FC<AncillaryModalProps> = ({
  selectedLeg,
  changeIsOverlayOpened,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const bookingId = useSelector(purchaseFlowBookingIdSelector);
  const checkoutForm = useFormContext<CheckoutValues>();
  const passengers = checkoutForm.watch('passengers');

  const form = useForm<AncillaryValues>({
    mode: 'onBlur',
    resolver: zodResolver(createAncillarySchema(t)),
    defaultValues: {
      selectedAncillaries: [],
    },
  });

  const { watch } = form;

  const selectedAncillaries = watch('selectedAncillaries');

  const totalAmount = useMemo(
    () =>
      selectedAncillaries.reduce(
        (sum, ancillary) =>
          sum +
          (ancillary?.amount || 0) *
            (ancillary?.passengersExternalReferences?.length || 0),
        0
      ),
    [selectedAncillaries]
  );

  const handleIsOverlayOpened = useCallback(
    (value: boolean) => {
      changeIsOverlayOpened(value);
      form.reset();
    },
    [changeIsOverlayOpened, form]
  );

  const onSubmit = useCallback(async () => {
    const response = await dispatch(
      addPurchaseFlowBookingAncillaries(selectedAncillaries)
    ).unwrap();

    if ('errors' in response) {
      response.errors.forEach((error) => toast.error(error.message));
    }

    await dispatch(getPurchaseFlowBookingById(bookingId)).unwrap();
    handleIsOverlayOpened(false);
  }, [dispatch, selectedAncillaries, bookingId, handleIsOverlayOpened]);

  return (
    <ResponsiveDialog
      open={Boolean(selectedLeg)}
      onOpenChange={handleIsOverlayOpened}
      title={<TransText i18nKey="addExtras" />}
      content={
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 p-4">
            <LegInfo leg={selectedLeg} />
            <Divider />
            <div className="flex flex-col justify-between gap-2">
              <div className="flex items-center justify-between">
                <Typography variant="subtitle">
                  <TransText i18nKey="selectExtras" />
                </Typography>
                <Typography variant="body2" className="text-neutral">
                  <TransText i18nKey="pricePerPassenger" />
                </Typography>
              </div>
              <AncillaryOptions
                selectedLeg={selectedLeg}
                passengers={passengers}
              />
            </div>
          </div>
        </Form>
      }
      footer={
        <AncillaryModalFooter
          totalAmount={totalAmount}
          onOverlayOpened={handleIsOverlayOpened}
          onSubmit={onSubmit}
          isSubmitDisabled={selectedAncillaries.length === 0}
        />
      }
    />
  );
};

export default AncillaryModal;
